// @owners { team: patients }
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { get } from '~shared/helpers/apiHelper';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { staleTimes } from '~shared/queries/constants';
import { useQuery } from '~shared/react-query';
import { type Facility } from '~shared/types/facility';

export type FacilityOptions = {
  label: string;
  value: string;
  data: Facility;
}[];

export const facilities = createQueryKeys('facilities', {
  fetchAll: {
    queryKey: null,
    queryFn: async (): Promise<Facility[]> => {
      const response = await get('/facilities');
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      return response;
    },
  },
});

const toFacilityOptions = (facilityList: Facility[]): FacilityOptions =>
  [...facilityList]
    // alphabetical by name; unnamed facilities sort last
    .sort((f1, f2) => {
      if (!f1.name) return 1;
      if (!f2.name) return -1;
      return f1.name.localeCompare(f2.name);
    })
    .map((facility) => ({
      label: facility.name ?? 'Unknown',
      value: facility.id.toString(),
      data: facility,
    }));

/**
 * Fetch Alto pharmacy facilities, mapped to InputSelect options (alphabetical by name).
 * Facilities change rarely, so this is cached aggressively (gcTime matches staleTime so
 * the entry survives unmounts for the full day).
 */
export const useFacilities = () => {
  const { data, isLoading, error } = useQuery({
    ...facilities.fetchAll,
    select: toFacilityOptions,
    staleTime: staleTimes.oneDay,
    gcTime: staleTimes.oneDay,
  });

  return {
    facilityOptions: data ?? [],
    isLoading,
    error,
  };
};
